// Vendor Libs
import React from 'react';
import PropTypes from 'prop-types';
import {get, isEmpty, pickBy} from 'lodash';

// Components
import Button from '../core/button';

// Styles
import './mapping-detail-buttons.scss';
import GraceNoteControlsGroup from './send-to-gracenote';

const mappingTabNames = ['BESTMATCHES', 'MOVIESANDPROGRAMSMAPPING', 'CREATESHELL'];

function MappingDetailButtons({
    cancelMapping,
    fetched,
    hasAccessByType,
    isTabActive,
    mapItem,
    mappingData,
    mappingGoToNextMediaItem,
    mappingResetContentType,
    mappingSetShowMessage,
    mappingSetShowRememberMapping,
    mappingSkipMassMapping,
    massMapping,
    massShell,
    rememberMappingData,
    selectFirstTab,
    showRememberMapping,
}) {
    const cancelMappingEpisode = () => {
        mappingResetContentType('episode');
        mappingSetShowMessage(false);
        mappingSetShowRememberMapping(false);
    };

    const cancelRememberMapping = () => {
        mappingSetShowRememberMapping(false);
    };

    const goToNextMediaItem = () => {
        const refreshServiceDataKey = isTabActive('UNMAPPEDMEDIAITEMS')
            ? 'unmappedMediaItems'
            : isTabActive('MEDIAITEMS')
              ? 'mediaItems'
              : undefined;

        mappingGoToNextMediaItem(refreshServiceDataKey);

        if (!massMapping) {
            selectFirstTab(mappingTabNames);
        }
    };

    const hasEpisode = () => get(mappingData, 'episode');

    const hasFranchise = () => get(mappingData, 'franchise');

    const hasWriteAccess = () => hasAccessByType({accessRequirement: 'write'});

    const isRecordShow = () => get(mappingData, 'franchise.echostarContentType') === 'SRM';

    const rememberMapping = () => {
        mappingSetShowRememberMapping(true);
    };

    const shouldDisableCompleteMapping = () =>
        fetched || (showRememberMapping && isEmpty(pickBy(rememberMappingData, (value) => value)));

    const skipMassMapping = () => {
        mappingSkipMassMapping();
    };

    const shouldFullyMap = () => !get(mappingData, 'episode') && isRecordShow() && !massMapping;

    const shouldRenderButton = (type) => {
        const conditional = {
            cancelMapping: () => !fetched && !massMapping && !massShell,
            cancelMappingEpisode: () => !fetched && hasFranchise() && hasEpisode() && !massShell,
            cancelMassMapping: () => massMapping || massShell,
            cancelRememberMapping: () => false && !fetched && showRememberMapping,
            completeMapping: () => !fetched && (!massMapping || hasEpisode() || massShell),
            createEpisodeShell: () => !fetched && massMapping && !hasEpisode() && !massShell,
            nextMediaItem: () => fetched,
            rememberMapping: () =>
                false &&
                !fetched &&
                !massMapping &&
                !showRememberMapping &&
                hasWriteAccess() &&
                !shouldFullyMap() &&
                !massShell,
            skipMassMapping: () => !fetched && (massMapping || massShell),
        };

        return conditional[type] ? conditional[type]() : false;
    };

    const renderButton = (type) => {
        const renderButtonProps = {
            cancelMassMapping: {
                children: 'Exit Mass Mapping',
                onClick: cancelMapping,
            },
            cancelMapping: {
                children: 'Cancel Mapping',
                onClick: cancelMapping,
            },
            cancelMappingEpisode: {
                children: 'Go Back',
                onClick: cancelMappingEpisode,
            },
            cancelRememberMapping: {
                children: 'Cancel Map & Remember',
                onClick: cancelRememberMapping,
            },
            completeMapping: {
                children: shouldFullyMap() ? 'Force Complete Mapping' : 'Complete Mapping',
                disabled: shouldDisableCompleteMapping(),
                onClick: mapItem,
            },
            nextMediaItem: {
                children: massMapping ? 'Map Next Episode' : 'Go To Next Media Item',
                onClick: goToNextMediaItem,
            },
            rememberMapping: {
                children: 'Map & Remember',
                onClick: rememberMapping,
            },
            skipMassMapping: {
                children: 'Skip Media Item',
                onClick: skipMassMapping,
            },
        };

        return shouldRenderButton(type) ? (
            <Button {...renderButtonProps[type]} className="mapping-detail-buttons--button" />
        ) : null;
    };

    return (
        <div className="mapping-detail-buttons--actions">
            {renderButton('completeMapping')}
            {renderButton('rememberMapping')}
            {renderButton('cancelRememberMapping')}
            {renderButton('nextMediaItem')}
            {renderButton('cancelMappingEpisode')}
            {renderButton('cancelMapping')}
            {renderButton('skipMassMapping')}
            {renderButton('cancelMassMapping')}
            <GraceNoteControlsGroup isMoviesAndPrograms={false} />
        </div>
    );
}

MappingDetailButtons.propTypes = {
    cancelMapping: PropTypes.func.isRequired,
    fetched: PropTypes.bool.isRequired,
    hasAccessByType: PropTypes.func.isRequired,
    isTabActive: PropTypes.func,
    mapItem: PropTypes.func.isRequired,
    mappingData: PropTypes.object,
    mappingGoToNextMediaItem: PropTypes.func.isRequired,
    mappingResetContentType: PropTypes.func.isRequired,
    mappingSetShowMessage: PropTypes.func.isRequired,
    mappingSetShowRememberMapping: PropTypes.func.isRequired,
    mappingSkipMassMapping: PropTypes.func.isRequired,
    massMapping: PropTypes.bool,
    massShell: PropTypes.bool,
    rememberMappingData: PropTypes.object,
    selectFirstTab: PropTypes.func,
    showRememberMapping: PropTypes.bool,
};

export default MappingDetailButtons;
